import { useEffect, useState } from "react";
import { Menu, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { NAV_LINKS, SITE, LOGO_IMAGE } from "@/data/site";
import { buildWhatsAppUrl } from "@/lib/whatsapp";
import { cn } from "@/lib/utils";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("inicio");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const sections = NAV_LINKS.map((l) => document.getElementById(l.id)).filter(Boolean);
    if (!sections.length) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  const goTo = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={cn(
        "fixed inset-x-0 top-0 z-[var(--z-nav)] transition-[background-color,box-shadow,border-color] duration-200",
        scrolled
          ? "border-b border-border bg-[rgba(250,248,244,0.78)] shadow-[0_8px_24px_rgba(20,16,12,0.06)] backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      )}
    >
      <div className="container-site flex h-[72px] items-center justify-between gap-6">
        <a
          href="#inicio"
          onClick={goTo("inicio")}
          className="flex items-center gap-3 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="navbar-logo-link"
          aria-label={`${SITE.name} - Início`}
        >
          <img
            src={LOGO_IMAGE}
            alt=""
            className="h-10 w-10 rounded-full border border-border object-cover"
            aria-hidden="true"
          />
          <div className="leading-none">
            <span className="block font-heading text-lg font-semibold tracking-[0.12em] text-foreground">
              RAMALIVE
            </span>
            <span className="block text-[10px] font-medium uppercase tracking-[0.3em] text-[hsl(var(--brand-taupe))]">
              Odontologia
            </span>
          </div>
        </a>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Navegação principal">
          {NAV_LINKS.map((link) => (
            <a
              key={link.id}
              href={`#${link.id}`}
              onClick={goTo(link.id)}
              data-testid={`navbar-link-${link.id}`}
              aria-current={active === link.id ? "page" : undefined}
              className={cn(
                "relative rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-200 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                active === link.id ? "text-foreground" : "text-muted-foreground hover:text-foreground"
              )}
            >
              {link.label}
              <span
                aria-hidden="true"
                className={cn(
                  "absolute inset-x-3 -bottom-0.5 h-px bg-[hsl(var(--brand-champagne-gold))] transition-opacity duration-200",
                  active === link.id ? "opacity-100" : "opacity-0"
                )}
              />
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <Button
            asChild
            className="h-11 rounded-xl px-5 active:scale-[0.98] transition-[background-color,transform] duration-200"
          >
            <a
              href={buildWhatsAppUrl()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="navbar-whatsapp-button"
            >
              <MessageCircle className="h-4 w-4" />
              Falar no WhatsApp
            </a>
          </Button>
        </div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="h-11 w-11 rounded-xl bg-card/70 lg:hidden"
              aria-label="Abrir menu"
              data-testid="navbar-mobile-menu-button"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[86vw] max-w-sm border-l border-border bg-background p-0">
            <div className="flex h-full flex-col">
              <div className="border-b border-border px-6 py-6">
                <SheetTitle className="font-heading text-xl font-semibold tracking-[0.12em]">RAMALIVE</SheetTitle>
                <SheetDescription className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--brand-taupe))]">
                  Odontologia
                </SheetDescription>
              </div>
              <nav className="flex flex-col gap-1 px-4 py-6" aria-label="Navegação mobile">
                {NAV_LINKS.map((link) => (
                  <a
                    key={link.id}
                    href={`#${link.id}`}
                    onClick={goTo(link.id)}
                    data-testid={`mobile-nav-link-${link.id}`}
                    className={cn(
                      "rounded-xl px-4 py-3 text-base font-medium transition-colors duration-200 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      active === link.id ? "bg-accent text-foreground" : "text-muted-foreground"
                    )}
                  >
                    {link.label}
                  </a>
                ))}
              </nav>
              <div className="mt-auto border-t border-border px-6 py-6">
                <Button asChild className="h-12 w-full rounded-xl">
                  <a
                    href={buildWhatsAppUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="mobile-nav-whatsapp-button"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Falar no WhatsApp
                  </a>
                </Button>
                <p className="mt-3 text-center text-xs text-muted-foreground">{SITE.whatsappDisplay}</p>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};
